import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { useIntl } from 'react-intl';

import { useDeactivateMembershipType } from 'src/features/membershipType/api/useDeactivateMembershipType';
import { MEMBERSHIP_TYPE_RESOURCE } from 'src/features/membershipType/api/useGetMembershipType';
import { MembershipType } from 'src/features/membershipType/types';
import { membershipTypeMessages } from 'src/features/membershipType/utils/messages';
import { toast } from 'src/hooks/UseToast';
import { queryClient } from 'src/lib/react-query';
import { useParking } from 'src/providers/ParkingProvider';
import ConfirmationModal from 'src/registry/components/blocks/ConfirmationModal';
import { DataTableNewEntryProps } from 'src/registry/components/table';
import { ICON_MAP } from 'src/utils/constants';

interface DeactivateMembershipTypeProps<TData> extends DataTableNewEntryProps<TData> {
    selectedTemplate?: MembershipType;
    children?: React.ReactNode;
}

function DeactivateMembershipType<TData>({ selectedTemplate, children }: DeactivateMembershipTypeProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const { formatMessage, locale } = useIntl();
    const { parkingId } = useParking();

    const { mutate: deactivateMembershipType } = useDeactivateMembershipType({
        onSuccess: (success) => {
            void queryClient.invalidateQueries({ queryKey: [MEMBERSHIP_TYPE_RESOURCE] });

            const templateName = success.translations?.find((membership) => membership.language === locale)?.name;

            toast({
                title: formatMessage(membershipTypeMessages.deactivatedTemplateSuccess),
                description: formatMessage(membershipTypeMessages.deactivatedTemplateSuccessDescription, {
                    entry: templateName,
                }),
            });
        },
    });

    if (!selectedTemplate) return null;

    return (
        <ConfirmationModal
            isModalOpen={isModalOpen}
            setIsModalOpen={setIsModalOpen}
            dialogDetails={{
                title: { ...membershipTypeMessages.deactivateTemplateModal },
                subTitle: {
                    ...membershipTypeMessages.deactivateTemplateSubtitle,
                    values: {
                        entry: selectedTemplate.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
                icon: ICON_MAP.WAVE_PULSE as IconProp,
                confirmationMessage: {
                    ...membershipTypeMessages.confirmDeactivateTemplate,
                    values: {
                        entry: selectedTemplate.translations?.find((temp) => temp.language === locale)?.name ?? '-',
                    },
                },
            }}
            callbackButton={{
                variant: 'destructiveLight',
                buttonTitle: membershipTypeMessages.deactivateTemplate,
                callbackFn: () => {
                    if (selectedTemplate) {
                        return (
                            deactivateMembershipType({ parkingId, typeId: selectedTemplate.id || '' }),
                            setIsModalOpen(false)
                        );
                    }
                },
            }}
        >
            {children}
        </ConfirmationModal>
    );
}

export default DeactivateMembershipType;
